import React, { useContext, useEffect, useState } from 'react';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '../../../components/ui/sheet';
import { Badge } from '../../../components/ui/badge';
import WebsiteSourceService from '../../../services/WebsiteSourceService/WebsiteSourceService';
import {
  CrawlRunResponse,
  WebsiteSourceResponse
} from '../../../types/WebsiteSource';
import { formatDateTime, runStatusVariant } from './websiteFormatting';

interface RunHistorySheetProps {
  source: WebsiteSourceResponse | null;
  onClose: () => void;
}

const COUNT_FIELDS: { key: keyof CrawlRunResponse; label: string }[] = [
  { key: 'discovered', label: 'discovered' },
  { key: 'fetched', label: 'fetched' },
  { key: 'created', label: 'new' },
  { key: 'changed', label: 'changed' },
  { key: 'unchanged', label: 'unchanged' },
  { key: 'gone', label: 'gone' },
  { key: 'outOfScope', label: 'out of scope' },
  { key: 'thin', label: 'thin' },
  { key: 'duplicate', label: 'duplicate' },
  { key: 'rejected', label: 'rejected' },
  { key: 'errored', label: 'errored' },
  { key: 'skippedCap', label: 'over limit' },
  { key: 'ownedElsewhere', label: 'owned elsewhere' },
  { key: 'retirementBlocked', label: 'retirement blocked' },
  { key: 'changeBlocked', label: 'changes held back' }
];

function RunHistorySheet({
  source,
  onClose
}: Readonly<RunHistorySheetProps>): React.ReactElement {
  const { currentChatbot } = useContext(ChatbotContext);
  const [runs, setRuns] = useState<CrawlRunResponse[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!source || !currentChatbot) return undefined;
    let cancelled = false;
    const load = (): void => {
      WebsiteSourceService.runs(currentChatbot.id, source.id)
        .then((data) => {
          if (!cancelled) {
            setRuns(data);
            setError(false);
          }
        })
        .catch(() => {
          if (!cancelled) setError(true);
        });
    };
    load();
    const interval = setInterval(load, 10000);
    return (): void => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [source, currentChatbot]);

  return (
    <Sheet open={source !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className='w-[560px] max-w-full overflow-y-auto'>
        <SheetHeader>
          <SheetTitle>Crawl history</SheetTitle>
          <SheetDescription className='break-all'>
            {source?.seedUrl}
          </SheetDescription>
        </SheetHeader>
        <div className='mt-4 flex flex-col gap-3'>
          {error && (
            <p className='text-sm text-destructive'>
              Unable to load crawl history.
            </p>
          )}
          {runs?.length === 0 && (
            <p className='text-sm text-muted-foreground'>
              No runs yet. The first crawl starts within a minute of saving.
            </p>
          )}
          {runs?.map((run) => (
            <div
              key={run.id}
              className='rounded-md border p-3 text-sm'
              data-testid='run-row'
            >
              <div className='flex items-center justify-between gap-2'>
                <Badge variant={runStatusVariant(run.status)}>
                  {run.status}
                </Badge>
                <span className='text-xs text-muted-foreground'>
                  {formatDateTime(run.startedAt ?? run.createdAt)}
                  {run.finishedAt && ` to ${formatDateTime(run.finishedAt)}`}
                </span>
              </div>
              <div className='mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs'>
                {COUNT_FIELDS.filter((field) => Number(run[field.key]) > 0).map(
                  (field) => (
                    <span key={field.key}>
                      <span className='font-medium'>
                        {String(run[field.key])}
                      </span>{' '}
                      {field.label}
                    </span>
                  )
                )}
                {run.capped && (
                  <span className='text-amber-700'>page limit reached</span>
                )}
              </div>
              {run.warning && (
                <p className='mt-1 text-xs text-amber-700'>
                  Warning: {run.warning}
                </p>
              )}
              {run.failureReason && (
                <p className='mt-1 text-xs text-destructive'>
                  {run.failureReason}
                </p>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default RunHistorySheet;
